"use client";

import { Blocks, BrainCircuit, Lock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
export default function Whyfuturereadyskills() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "Digital Confidence",
    icon: Blocks,
    description: "We help children, young people and community members feel more confident using technology, digital tools and online learning in everyday life."
  }, {
    number: "02",
    title: "Creative Thinking",
    icon: BrainCircuit,
    description: "Our programs encourage curiosity, imagination and new ways of solving problems through hands-on, practical and engaging learning experiences."
  }, {
    number: "03",
    title: "Problem Solving",
    icon: Lock,
    description: "We support learners to build practical thinking skills that can help them at school, at work and in the community."
  }];
  return <section data-section-id="5200" id="whyfuturereadyskills" className="bg-background text-foreground py-32 md:py-50 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-4">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-xs md:text-sm tracking-widest uppercase text-primary">
                WHY IT MATTERS
              </span>
              <div className="h-px w-16 bg-primary" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-foreground">
              Why Future-Ready Skills Matter
            </h2>
            <p className="mt-8 font-serif text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Technology is changing the way we learn, work and live. Future Ready Australia helps children, young people and communities build confidence, creativity and practical skills for the future.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-6 w-full">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={i} pillar={pillar} Icon={Icon} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  pillar,
  Icon,
  index
}: {
  pillar: {
    number: string;
    title: string;
    description: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`transition-all duration-700 h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <Card className="group relative h-full min-h-[420px] bg-card backdrop-blur-none border border-border rounded-lg p-10 md:p-12 flex flex-col shadow-md hover:border-primary hover:shadow-xl transition-all duration-500">
        <div className="font-mono text-5xl md:text-6xl font-bold text-primary/20 transition-transform duration-500 group-hover:scale-110 group-hover:text-primary origin-left">
          {pillar.number}
        </div>

        <div className="h-px w-full bg-border mt-6 mb-10 group-hover:bg-primary/30 transition-colors duration-500" />

        <Icon className="size-8 text-muted-foreground group-hover:text-primary transition-colors duration-500 mb-8" strokeWidth={1.5} />

        <h3 className="font-default font-bold text-2xl md:text-3xl tracking-tight mb-6 leading-tight text-foreground">
          {pillar.title}
        </h3>

        <p className="font-serif text-base md:text-[17px] leading-relaxed text-muted-foreground mb-10 flex-grow">
          {pillar.description}
        </p>
      </Card>
    </div>;
}